'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

const ResponsiveModal = DialogPrimitive.Root

const ResponsiveModalTrigger = DialogPrimitive.Trigger

const ResponsiveModalClose = DialogPrimitive.Close

const ResponsiveModalPortal = DialogPrimitive.Portal

const ResponsiveModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-[102] bg-neutral-900/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
    ref={ref}
  />
))
ResponsiveModalOverlay.displayName = DialogPrimitive.Overlay.displayName

const ResponsiveModalVariants = cva(
  cn(
    'fixed z-[102] gap-4 bg-background p-6 pb-8 transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
    'lg:left-[50%] lg:top-[50%] lg:w-full lg:max-w-lg lg:translate-x-[-50%] lg:translate-y-[-50%] lg:duration-200 lg:data-[state=open]:animate-in lg:data-[state=closed]:animate-out lg:data-[state=closed]:fade-out-0 lg:data-[state=open]:fade-in-0 lg:data-[state=closed]:zoom-out-95 lg:data-[state=open]:zoom-in-95 lg:data-[state=closed]:slide-out-to-left-1/2 lg:data-[state=closed]:slide-out-to-top-[48%] lg:data-[state=open]:slide-in-from-left-1/2 lg:data-[state=open]:slide-in-from-top-[48%] lg:rounded-xl'
  ),
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b rounded-b-xl max-h-[90dvh] lg:h-fit data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t lg:h-fit max-h-[90dvh] rounded-t-xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full lg:h-fit w-3/4 border-r rounded-r-xl data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
        right:
          'inset-y-0 right-0 h-full lg:h-fit w-3/4 border-l rounded-l-xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
        center: 'inset-x-0 top-1/2 -translate-y-1/2 lg:w-fit lg:max-w-sm',
      },
    },
    defaultVariants: {
      side: 'bottom',
    },
  }
)

interface ResponsiveModalContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof ResponsiveModalVariants> {
  overlayClassName?: string
  closeClassName?: string
}

const ResponsiveModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ResponsiveModalContentProps
>(
  (
    {
      side = 'bottom',
      className,
      children,
      overlayClassName,
      closeClassName,
      ...props
    },
    ref
  ) => {
    // Extract overflow classes from className to separate scrollable container
    const classNameStr = className || ''
    const hasOverflow = classNameStr.includes('overflow-y-auto')

    // Extract only overflow and max-height classes for inner wrapper (not max-w, as it should stay on outer)
    const overflowClasses = classNameStr.match(/(?:overflow|max-h)[^ ]*/g) || []
    const otherClasses = classNameStr
      .replace(/(?:overflow|max-h)[^ ]*/g, '')
      .trim()
      .replace(/\s+/g, ' ')

    return (
      <ResponsiveModalPortal>
        <ResponsiveModalOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            ResponsiveModalVariants({ side }),
            hasOverflow ? cn('flex flex-col', otherClasses) : classNameStr
          )}
          {...props}
        >
          {hasOverflow ? (
            <div
              className={cn(overflowClasses.join(' '))}
              style={{ height: '100%' }}
            >
              {children}
            </div>
          ) : (
            children
          )}
          <ResponsiveModalClose
            className={cn(
              'absolute right-1 top-1 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-light bg-neutral-900 text-light shadow-[0px_3.63px_4.54px_0px_rgba(0,0,0,0.25)] hover:bg-neutral-800 focus-visible:outline-none disabled:pointer-events-none lg:-right-4 lg:-top-4',
              closeClassName
            )}
          >
            <X className='h-4 w-4' strokeWidth={3} />
            <span className='sr-only'>Close</span>
          </ResponsiveModalClose>
        </DialogPrimitive.Content>
      </ResponsiveModalPortal>
    )
  }
)
ResponsiveModalContent.displayName = DialogPrimitive.Content.displayName

const ResponsiveModalHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-2 text-center sm:text-left',
      className
    )}
    {...props}
  />
)
ResponsiveModalHeader.displayName = 'ResponsiveModalHeader'

const ResponsiveModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
)
ResponsiveModalFooter.displayName = 'ResponsiveModalFooter'

const ResponsiveModalTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
))
ResponsiveModalTitle.displayName = DialogPrimitive.Title.displayName

const ResponsiveModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
ResponsiveModalDescription.displayName = DialogPrimitive.Description.displayName

export {
  ResponsiveModal,
  ResponsiveModalClose,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
  ResponsiveModalHeader,
  ResponsiveModalOverlay,
  ResponsiveModalPortal,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
}
